import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { useToast } from '../../lib/toast';
import { Drawer, drawerStyles } from '../../components/Drawer';
import { Select } from '../../components/Select';
import { StatusPill } from '../../components/StatusPill';
import { CreatorActionBadge } from '../../components/StatusBadge';
import { formatFollowers } from '../../lib/format';
import type { Creator, PlatformHandle } from '../../lib/types';
import styles from './Dashboard.module.css';

type SortCol = 'name' | 'platform' | 'followers' | 'persona' | 'contact' | 'status' | 'updated';
type SortDir = 'asc' | 'desc';

const DEFAULT_DIR: Record<SortCol, SortDir> = {
  followers: 'desc',
  updated: 'desc',
  name: 'asc',
  platform: 'asc',
  persona: 'asc',
  contact: 'asc',
  status: 'asc',
};

const PERSONA_ORDER = [
  'Peak Performers Under Pressure',
  'High-Achieving Students',
  'Wired-but-Weary Wellness Seekers',
  'Parents of Young or Neurodivergent Kids',
  'Sensory-Sensitive Strivers',
];

const STATUS_ORDER = [
  'Needs Response',
  'Follow Up',
  'Approved',
  'In Negotiation',
  'Ready for Onboarding Form',
  'Cut',
  'Contracted',
];

const PERSONAS = [
  'Peak Performers Under Pressure',
  'Wired-but-Weary Wellness Seekers',
  'High-Achieving Students',
  'Parents of Young or Neurodivergent Kids',
  'Sensory-Sensitive Strivers',
];

const PH_PLATFORMS = [
  'TikTok',
  'Instagram',
  'YouTube',
  'Facebook',
  'X (Twitter)',
  'LinkedIn',
  'Snapchat',
  'Other',
];

const STATUS_BTNS: { key: string; cls: string }[] = [
  { key: 'Follow Up', cls: styles.sbFollowUp },
  { key: 'Needs Response', cls: styles.sbNeedsResponse },
  { key: 'Approved', cls: styles.sbApproved },
  { key: 'In Negotiation', cls: styles.sbInNegotiation },
  { key: 'Cut', cls: styles.sbCut },
  { key: 'Ready for Onboarding Form', cls: styles.sbReadyOnboarding },
];

const STATUS_TINT: Record<string, string> = {
  'Follow Up': styles.statusFollowUp,
  'Needs Response': styles.statusNeedsResponse,
  Approved: styles.statusApproved,
  Cut: styles.statusCut,
  'In Negotiation': styles.statusInNegotiation,
  Contracted: styles.statusContracted,
  'Ready for Onboarding Form': styles.statusReadyOnboarding,
};

// Short canonical code from a platform name. Drives platform chips and the
// "All Platforms / TT / IG / YT / FB" filter dropdown.
function platformCode(name: string): string {
  const n = name.toLowerCase();
  if (n.includes('tiktok')) return 'TT';
  if (n.includes('instagram')) return 'IG';
  if (n.includes('youtube')) return 'YT';
  if (n.includes('facebook')) return 'FB';
  if (n.startsWith('x') || n.includes('twitter')) return 'X';
  if (n.includes('linkedin')) return 'LI';
  if (n.includes('snap')) return 'SC';
  return name.slice(0, 4).toUpperCase();
}

function chipClass(code: string): string {
  return (
    {
      TT: styles.chipTT,
      IG: styles.chipIG,
      YT: styles.chipYT,
      FB: styles.chipFB,
    }[code] ?? styles.chipOther
  );
}

function shortPersona(p: string | undefined): string {
  if (!p) return '—';
  const map: Record<string, string> = {
    'Peak Performers Under Pressure': 'Peak Performers',
    'Wired-but-Weary Wellness Seekers': 'Wellness Seekers',
    'High-Achieving Students': 'High-Achieving Students',
    'Parents of Young or Neurodivergent Kids': 'Parents / Neurodivergent',
    'Sensory-Sensitive Strivers': 'Sensory-Sensitive',
  };
  for (const [k, v] of Object.entries(map)) if (p.includes(k)) return v;
  return p.length > 28 ? p.slice(0, 28) + '…' : p;
}

function PlatformChips({ handles }: { handles: PlatformHandle[] }) {
  if (handles.length === 0) return <span style={{ color: '#94a3b8' }}>—</span>;
  // Deduplicate by platform code so a creator on TT in two regions doesn't render two TT chips.
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const h of handles) {
    const c = platformCode(h.platformName);
    if (!seen.has(c)) {
      seen.add(c);
      codes.push(c);
    }
  }
  return (
    <div className={styles.chips}>
      {codes.map((c) => (
        <span key={c} className={`${styles.chip} ${chipClass(c)}`}>
          {c}
        </span>
      ))}
    </div>
  );
}

function primaryHandle(handles: PlatformHandle[]): string {
  if (handles.length === 0) return '—';
  const h = handles[0].handle.startsWith('@') ? handles[0].handle : '@' + handles[0].handle;
  return h;
}

function maxFollowers(handles: PlatformHandle[]): number {
  return handles.reduce((m, h) => Math.max(m, h.followerCount || 0), 0);
}

function sumFollowers(handles: PlatformHandle[]): number {
  return handles.reduce((m, h) => m + (h.followerCount || 0), 0);
}

export function CreatorDiscoveryTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'prospects'],
    queryFn: () => api.get<Creator[]>('/api/admin/prospects'),
  });
  const creators = useMemo(() => data ?? [], [data]);

  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterPlatform, setFilterPlatform] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortCol, setSortCol] = useState<SortCol>('followers');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const [form, setForm] = useState<Partial<Creator>>({});
  const [drawerStatus, setDrawerStatus] = useState<string>('Follow Up');
  // Working copy of the handles for the open drawer. Diffed against
  // selected.platformHandles on Save to compute the POST/PATCH/DELETE set.
  const [localHandles, setLocalHandles] = useState<PlatformHandle[]>([]);
  const [saving, setSaving] = useState(false);

  const filteredSorted = useMemo(() => {
    let list = [...creators];
    if (filterStatus) list = list.filter((c) => c.action === filterStatus);
    if (filterPlatform) {
      list = list.filter((c) =>
        c.platformHandles.some((h) => platformCode(h.platformName) === filterPlatform),
      );
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (c) =>
          (c.name && c.name.toLowerCase().includes(q)) ||
          (c.contact && c.contact.toLowerCase().includes(q)) ||
          (c.persona && c.persona.toLowerCase().includes(q)) ||
          (c.notes && c.notes.toLowerCase().includes(q)) ||
          c.platformHandles.some(
            (h) =>
              h.handle.toLowerCase().includes(q) || h.platformName.toLowerCase().includes(q),
          ),
      );
    }
    const dir = sortDir === 'asc' ? 1 : -1;
    const cmpStr = (a: string, b: string) =>
      !a && !b ? 0 : !a ? 1 : !b ? -1 : a.localeCompare(b) * dir;
    switch (sortCol) {
      case 'name':
        list.sort((a, b) => cmpStr(a.name, b.name));
        break;
      case 'contact':
        list.sort((a, b) => cmpStr(a.contact, b.contact));
        break;
      case 'followers':
        list.sort((a, b) => (maxFollowers(a.platformHandles) - maxFollowers(b.platformHandles)) * dir);
        break;
      case 'platform': {
        const fp = (c: Creator) =>
          c.platformHandles[0] ? platformCode(c.platformHandles[0].platformName) : '';
        list.sort((a, b) => cmpStr(fp(a), fp(b)));
        break;
      }
      case 'persona': {
        const rank = (p: string) => {
          if (!p) return PERSONA_ORDER.length;
          const idx = PERSONA_ORDER.findIndex((o) => p.includes(o));
          return idx === -1 ? PERSONA_ORDER.length : idx;
        };
        list.sort(
          (a, b) =>
            (rank(a.persona) - rank(b.persona)) * dir || (a.name || '').localeCompare(b.name || ''),
        );
        break;
      }
      case 'status':
        list.sort((a, b) => {
          const ra = STATUS_ORDER.indexOf(a.action);
          const rb = STATUS_ORDER.indexOf(b.action);
          return (
            ((ra === -1 ? STATUS_ORDER.length : ra) - (rb === -1 ? STATUS_ORDER.length : rb)) *
              dir || (a.name || '').localeCompare(b.name || '')
          );
        });
        break;
      case 'updated':
        list.sort((a, b) => cmpStr(a.lastUpdated || '', b.lastUpdated || ''));
        break;
    }
    return list;
  }, [creators, filterStatus, filterPlatform, searchQuery, sortCol, sortDir]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {
      'Follow Up': 0,
      'Needs Response': 0,
      Approved: 0,
      Cut: 0,
      'Ready for Onboarding Form': 0,
    };
    creators.forEach((cr) => {
      if (cr.action in c) c[cr.action]++;
    });
    return c;
  }, [creators]);

  const selected = useMemo(
    () => creators.find((c) => c.id === selectedId) ?? null,
    [creators, selectedId],
  );

  useEffect(() => {
    if (selected) {
      setForm(selected);
      setDrawerStatus(selected.action || 'Follow Up');
      setLocalHandles(selected.platformHandles.map((h) => ({ ...h })));
    }
  }, [selected]);

  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/admin/prospects/${id}`),
    onSuccess: (_void, id) => {
      qc.setQueryData<Creator[]>(['admin', 'prospects'], (prev) =>
        prev?.filter((c) => c.id !== id) ?? [],
      );
      setSelectedId(null);
      toast('Deleted');
    },
    onError: () => toast('Error deleting'),
  });

  function clickSort(col: SortCol) {
    if (sortCol === col) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir(DEFAULT_DIR[col]);
    }
  }

  async function save() {
    if (!selectedId || !selected) return;
    setSaving(true);
    let failures = 0;
    try {
      // 1. Main creator update first — sets the baseline so the cache update later doesn't
      //    flicker old values.
      await api.put<Creator>(`/api/admin/prospects/${selectedId}`, {
        name: form.name?.trim() || '',
        persona: form.persona?.trim() || '',
        socialLink: form.socialLink?.trim() || '',
        contact: form.contact?.trim() || '',
        outreachDate: form.outreachDate?.trim() || '',
        outreachChannel: form.outreachChannel?.trim() || '',
        response: form.response || '',
        followUpDate: form.followUpDate?.trim() || '',
        followUpResponse: form.followUpResponse || '',
        notes: form.notes || '',
        action: drawerStatus,
      });
    } catch {
      failures++;
    }

    // 2. Diff local handles vs server source of truth.
    const sourceById = new Map(selected.platformHandles.map((h) => [h.id, h]));
    const localById = new Map(
      localHandles.filter((h) => !h.id.startsWith('new:')).map((h) => [h.id, h]),
    );
    const toCreate = localHandles.filter((h) => h.id.startsWith('new:'));
    const toUpdate = localHandles.filter((h) => {
      if (h.id.startsWith('new:')) return false;
      const src = sourceById.get(h.id);
      if (!src) return false;
      return (
        src.platformName !== h.platformName ||
        src.handle !== h.handle ||
        src.followerCount !== h.followerCount ||
        src.platformLink !== h.platformLink
      );
    });
    const toDelete = selected.platformHandles.filter((h) => !localById.has(h.id));

    // 3. Fan out handle mutations. Use Promise.allSettled so a single failure doesn't abort
    //    the rest — the user can retry the failed rows after.
    const results = await Promise.allSettled([
      ...toCreate.map((h) =>
        api.post<PlatformHandle>(`/api/admin/prospects/${selectedId}/handles`, {
          platformName: h.platformName,
          handle: h.handle,
          followerCount: h.followerCount,
          platformLink: h.platformLink,
        }),
      ),
      ...toUpdate.map((h) =>
        api.patch<PlatformHandle>(`/api/admin/prospects/${selectedId}/handles/${h.id}`, {
          platformName: h.platformName,
          handle: h.handle,
          followerCount: h.followerCount,
          platformLink: h.platformLink,
        }),
      ),
      ...toDelete.map((h) =>
        api.delete<void>(`/api/admin/prospects/${selectedId}/handles/${h.id}`),
      ),
    ]);
    failures += results.filter((r) => r.status === 'rejected').length;

    // 4. Refetch the list so the cache (and the drawer's source of truth) reflect the
    //    real server state — including any partial-failure rows still as they were.
    await qc.invalidateQueries({ queryKey: ['admin', 'prospects'] });
    setSaving(false);
    toast(failures === 0 ? 'Saved!' : `Saved with ${failures} error${failures === 1 ? '' : 's'}`);
  }

  function del() {
    if (!selectedId) return;
    if (!confirm(`Delete "${selected?.name}"? This cannot be undone.`)) return;
    remove.mutate(selectedId);
  }

  const dirLabel = useMemo(() => {
    const m: Partial<Record<SortCol, string>> = {
      followers: sortDir === 'desc' ? 'High → Low' : 'Low → High',
      updated: sortDir === 'desc' ? 'Newest first' : 'Oldest first',
      status: sortDir === 'asc' ? 'Group A → Z' : 'Group Z → A',
      persona: sortDir === 'asc' ? 'Group A → Z' : 'Group Z → A',
      platform: sortDir === 'asc' ? 'A → Z' : 'Z → A',
    };
    return m[sortCol] ?? (sortDir === 'asc' ? 'A → Z' : 'Z → A');
  }, [sortCol, sortDir]);

  return (
    <>
      <div className={styles.summaryBar}>
        <StatusPill
          color="blue"
          label="Follow Up"
          count={counts['Follow Up']}
          active={filterStatus === 'Follow Up'}
          onClick={() => setFilterStatus(filterStatus === 'Follow Up' ? null : 'Follow Up')}
        />
        <StatusPill
          color="amber"
          label="Needs Response"
          count={counts['Needs Response']}
          active={filterStatus === 'Needs Response'}
          onClick={() =>
            setFilterStatus(filterStatus === 'Needs Response' ? null : 'Needs Response')
          }
        />
        <StatusPill
          color="green"
          label="Approved"
          count={counts['Approved']}
          active={filterStatus === 'Approved'}
          onClick={() => setFilterStatus(filterStatus === 'Approved' ? null : 'Approved')}
        />
        <StatusPill
          color="red"
          label="Cut"
          count={counts['Cut']}
          active={filterStatus === 'Cut'}
          onClick={() => setFilterStatus(filterStatus === 'Cut' ? null : 'Cut')}
        />
        <StatusPill
          color="teal"
          label="Ready for Onboarding"
          count={counts['Ready for Onboarding Form']}
          active={filterStatus === 'Ready for Onboarding Form'}
          onClick={() =>
            setFilterStatus(
              filterStatus === 'Ready for Onboarding Form' ? null : 'Ready for Onboarding Form',
            )
          }
        />
        <span className={styles.summaryTotal}>
          {filteredSorted.length} of {creators.length} creators
        </span>
      </div>

      <div className={styles.filterBar}>
        <input
          className={styles.search}
          type="text"
          placeholder="🔍  Search by name, handle, platform, notes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <Select
          className={styles.filterSelect}
          value={filterPlatform}
          onChange={(e) => setFilterPlatform(e.target.value)}
        >
          <option value="">All Platforms</option>
          <option value="TT">TikTok</option>
          <option value="IG">Instagram</option>
          <option value="YT">YouTube</option>
          <option value="FB">Facebook</option>
          <option value="X">X (Twitter)</option>
          <option value="LI">LinkedIn</option>
          <option value="SC">Snapchat</option>
        </Select>
        <button
          type="button"
          className={styles.btnClear}
          onClick={() => {
            setSearchQuery('');
            setFilterStatus(null);
            setFilterPlatform('');
            setSortCol('followers');
            setSortDir('desc');
          }}
        >
          Clear Filters
        </button>
        <span className={styles.sortHint}>
          Sorted by {sortCol}: {dirLabel}
        </span>
      </div>

      <div className={styles.tableWrap}>
        {isLoading ? (
          <div className={styles.noResults}>Loading…</div>
        ) : filteredSorted.length === 0 ? (
          <div className={styles.noResults}>
            <div className={styles.noResultsIcon}>🔍</div>
            <div>No creators match your filters</div>
          </div>
        ) : (
          <table className={styles.table}>
            <thead>
              <tr>
                {(
                  [
                    ['name', 'Name'],
                    ['platform', 'Platforms'],
                    ['followers', 'Followers'],
                    ['persona', 'Persona'],
                    ['contact', 'Contact'],
                    ['status', 'Status'],
                    ['updated', 'Updated'],
                  ] as [SortCol, string][]
                ).map(([col, label]) => (
                  <th
                    key={col}
                    className={`${styles.sortable} ${sortCol === col ? styles.sorted : ''}`}
                    onClick={() => clickSort(col)}
                  >
                    {label}
                    <span className={styles.si}>
                      {sortCol === col ? (sortDir === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredSorted.map((c) => (
                <tr
                  key={c.id}
                  className={`${selectedId === c.id ? styles.selected : ''} ${STATUS_TINT[c.action] ?? ''}`}
                  onClick={() => setSelectedId(c.id)}
                >
                  <td className={styles.tdName}>
                    {c.name || '—'}
                    {c.platformHandles[0] && (
                      <div className={styles.tdHandle}>{primaryHandle(c.platformHandles)}</div>
                    )}
                  </td>
                  <td>
                    <PlatformChips handles={c.platformHandles} />
                  </td>
                  <td className={styles.tdFollowers}>
                    {c.platformHandles.length > 0
                      ? formatFollowers(maxFollowers(c.platformHandles))
                      : '—'}
                  </td>
                  <td className={styles.tdSmall}>{shortPersona(c.persona)}</td>
                  <td className={styles.tdSmall}>{c.contact || '—'}</td>
                  <td>
                    <CreatorActionBadge status={c.action} />
                  </td>
                  <td className={styles.tdUpdated}>{c.lastUpdated || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Drawer
        open={!!selected}
        onClose={() => setSelectedId(null)}
        header={
          <div className={drawerStyles.header}>
            <button
              type="button"
              className={drawerStyles.closeBtn}
              onClick={() => setSelectedId(null)}
            >
              ← Back
            </button>
            <div className={drawerStyles.actions}>
              <button
                type="button"
                className={drawerStyles.saveBtn}
                onClick={save}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" className={drawerStyles.dangerBtn} onClick={del}>
                Delete
              </button>
            </div>
          </div>
        }
      >
        {selected && (
          <>
            <div className={drawerStyles.titleBlock}>
              <h2>{selected.name || 'Unnamed'}</h2>
              <PlatformChips handles={selected.platformHandles} />
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 5 }}>
                {selected.lastUpdated ? `Last updated ${selected.lastUpdated}` : ''}
                {selected.platformHandles.length > 0 && (
                  <span>
                    {' '}
                    · {formatFollowers(sumFollowers(selected.platformHandles))} followers across{' '}
                    {selected.platformHandles.length} platform
                    {selected.platformHandles.length === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Status</div>
              <div className={styles.statusBtns}>
                {STATUS_BTNS.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    className={`${styles.statusBtn} ${drawerStatus === s.key ? s.cls : ''}`}
                    onClick={() => setDrawerStatus(s.key)}
                  >
                    {s.key}
                  </button>
                ))}
              </div>
            </div>

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Basic Info</div>
              <div className={styles.fgrid}>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label>Name</label>
                  <input
                    value={form.name ?? ''}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                  />
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label>Persona / Content Type</label>
                  <Select
                    value={form.persona ?? ''}
                    onChange={(e) => setForm({ ...form, persona: e.target.value })}
                    placeholder="Select persona…"
                  >
                    {PERSONAS.map((p) => (
                      <option key={p}>{p}</option>
                    ))}
                  </Select>
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label>Social Link</label>
                  <div className={styles.linkRow}>
                    <input
                      value={form.socialLink ?? ''}
                      onChange={(e) => setForm({ ...form, socialLink: e.target.value })}
                    />
                    <a
                      className={styles.linkGo}
                      href={form.socialLink || '#'}
                      target="_blank"
                      rel="noreferrer"
                    >
                      ↗
                    </a>
                  </div>
                </div>
                <div className={`${styles.fitem} ${styles.fitemFull}`}>
                  <label>Contact Email</label>
                  <input
                    value={form.contact ?? ''}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <PlatformsSection handles={localHandles} setHandles={setLocalHandles} />

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Outreach</div>
              <div className={styles.fgrid}>
                <div className={styles.fitem}>
                  <label>Outreach Date</label>
                  <input
                    value={form.outreachDate ?? ''}
                    onChange={(e) => setForm({ ...form, outreachDate: e.target.value })}
                    placeholder="MM/DD/YYYY"
                  />
                </div>
                <div className={styles.fitem}>
                  <label>Channel</label>
                  <input
                    value={form.outreachChannel ?? ''}
                    onChange={(e) => setForm({ ...form, outreachChannel: e.target.value })}
                    placeholder="e.g. DM, Email"
                  />
                </div>
              </div>
            </div>

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Creator Response</div>
              <textarea
                className={styles.textarea}
                rows={7}
                value={form.response ?? ''}
                onChange={(e) => setForm({ ...form, response: e.target.value })}
                placeholder="Paste the creator's response here…"
              />
            </div>

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Follow Up</div>
              <div className={styles.fgrid} style={{ marginBottom: 10 }}>
                <div className={styles.fitem}>
                  <label>Follow Up Date</label>
                  <input
                    value={form.followUpDate ?? ''}
                    onChange={(e) => setForm({ ...form, followUpDate: e.target.value })}
                    placeholder="MM/DD/YYYY"
                  />
                </div>
              </div>
              <label className={styles.subLabel}>Follow Up Notes</label>
              <textarea
                className={styles.textarea}
                rows={4}
                value={form.followUpResponse ?? ''}
                onChange={(e) => setForm({ ...form, followUpResponse: e.target.value })}
                placeholder="Notes from follow up…"
              />
            </div>

            <div className={drawerStyles.section}>
              <div className={drawerStyles.sectionLabel}>Notes</div>
              <textarea
                className={styles.textarea}
                rows={5}
                value={form.notes ?? ''}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Internal notes…"
              />
            </div>
          </>
        )}
      </Drawer>
    </>
  );
}

// Pure controlled list. Doesn't fire any API calls — all changes flow to setHandles
// and get persisted when the parent drawer's Save button fans out the diff.
function PlatformsSection({
  handles,
  setHandles,
}: {
  handles: PlatformHandle[];
  setHandles: (next: PlatformHandle[]) => void;
}) {
  const [editingId, setEditingId] = useState<string | null>(null);

  function newId(): string {
    return `new:${Math.random().toString(36).slice(2, 10)}`;
  }

  function addBlank() {
    const id = newId();
    setHandles([
      ...handles,
      { id, platformName: '', handle: '', followerCount: 0, platformLink: '' },
    ]);
    setEditingId(id);
  }

  function update(id: string, patch: Partial<PlatformHandle>) {
    setHandles(handles.map((h) => (h.id === id ? { ...h, ...patch } : h)));
  }

  function removeRow(id: string) {
    setHandles(handles.filter((h) => h.id !== id));
    if (editingId === id) setEditingId(null);
  }

  return (
    <div className={drawerStyles.section}>
      <div className={drawerStyles.sectionLabel}>Platforms</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {handles.length === 0 ? (
          <div style={{ fontSize: 13, color: 'var(--muted)' }}>
            No platforms yet — add one below.
          </div>
        ) : (
          handles.map((h) =>
            editingId === h.id ? (
              <HandleEditCard
                key={h.id}
                value={h}
                onChange={(patch) => update(h.id, patch)}
                onDone={() => setEditingId(null)}
                onDelete={() => removeRow(h.id)}
              />
            ) : (
              <div
                key={h.id}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '90px minmax(120px,1fr) auto auto',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 12px',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  background: '#fff',
                }}
              >
                <span className={`${styles.chip} ${chipClass(platformCode(h.platformName))}`}>
                  {h.platformName || '—'}
                </span>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13 }}>
                    {h.handle ? (h.handle.startsWith('@') ? h.handle : '@' + h.handle) : '—'}
                  </div>
                  {h.platformLink && (
                    <a
                      href={h.platformLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        fontSize: 12,
                        color: 'var(--text2)',
                        textDecoration: 'underline',
                        textUnderlineOffset: 2,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        display: 'block',
                        maxWidth: '100%',
                      }}
                    >
                      {h.platformLink}
                    </a>
                  )}
                </div>
                <span style={{ fontSize: 12, color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                  {formatFollowers(h.followerCount)} followers
                </span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button
                    type="button"
                    className={styles.btnClear}
                    style={{ padding: '4px 10px', fontSize: 12 }}
                    onClick={() => setEditingId(h.id)}
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className={styles.btnClear}
                    style={{ padding: '4px 10px', fontSize: 12, color: 'var(--red)' }}
                    onClick={() => removeRow(h.id)}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ),
          )
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <button type="button" className={styles.btnClear} onClick={addBlank}>
          + Add a platform
        </button>
      </div>
      <p style={{ fontSize: 11, color: 'var(--muted)', marginTop: 8, fontStyle: 'italic' }}>
        Changes are kept locally until you hit Save at the top.
      </p>
    </div>
  );
}

function HandleEditCard({
  value,
  onChange,
  onDone,
  onDelete,
}: {
  value: PlatformHandle;
  onChange: (patch: Partial<PlatformHandle>) => void;
  onDone: () => void;
  onDelete: () => void;
}) {
  const known = PH_PLATFORMS.slice(0, -1);
  const isOther = value.platformName && !known.includes(value.platformName);
  const [selectedOpt, setSelectedOpt] = useState(isOther ? 'Other' : value.platformName);
  const [other, setOther] = useState(isOther ? value.platformName : '');

  function pickPlatform(sel: string, otherVal: string) {
    setSelectedOpt(sel);
    setOther(otherVal);
    onChange({ platformName: sel === 'Other' ? otherVal : sel });
  }

  return (
    <div
      style={{
        border: '1px solid var(--accent)',
        borderRadius: 10,
        padding: 12,
        background: '#fff',
      }}
    >
      <div className={styles.fgrid}>
        <div className={styles.fitem}>
          <label>Platform</label>
          <Select
            value={selectedOpt}
            onChange={(e) => pickPlatform(e.target.value, other)}
            placeholder="Platform…"
          >
            {PH_PLATFORMS.map((p) => (
              <option key={p}>{p}</option>
            ))}
          </Select>
        </div>
        {selectedOpt === 'Other' && (
          <div className={styles.fitem}>
            <label>Platform name</label>
            <input value={other} onChange={(e) => pickPlatform('Other', e.target.value)} />
          </div>
        )}
        <div className={styles.fitem}>
          <label>Handle</label>
          <input
            value={value.handle}
            onChange={(e) => onChange({ handle: e.target.value })}
            placeholder="@handle"
          />
        </div>
        <div className={styles.fitem}>
          <label>Followers</label>
          <input
            type="number"
            value={value.followerCount}
            onChange={(e) => onChange({ followerCount: parseInt(e.target.value, 10) || 0 })}
          />
        </div>
        <div className={`${styles.fitem} ${styles.fitemFull}`}>
          <label>Link</label>
          <input
            type="url"
            value={value.platformLink}
            onChange={(e) => onChange({ platformLink: e.target.value })}
            placeholder="https://…"
          />
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 10 }}>
        <button
          type="button"
          className={styles.btnClear}
          style={{ color: 'var(--red)' }}
          onClick={onDelete}
        >
          Remove
        </button>
        <button type="button" className={styles.btnClear} onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
